import { useCallback, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Amount, Ltr } from '@/components/amount';
import { ConfirmBox } from '@/components/confirm-box';

/**
 * THE FROZEN RATE, ON SCREEN — the five wire fields of `FrozenRateFieldsSchema`
 * drawn the same way on every document that carries them.
 *
 * The server half of EC-19-10 put `exchangeRate`, `rateEffectiveOn`,
 * `baseCurrency`, `baseTotal` and `rateWarning` on all nine document
 * responses. Nine hand-copied panels would be nine chances for one document to
 * quietly stop citing its "as of" date — the same argument that made
 * `frozenRateFields` one schema and `frozenRateColumns` one migration helper —
 * so it is one component, and BR-SAL-04-09's "the 'as of' date must be shown"
 * is satisfied for a document by rendering this.
 *
 * ── WHAT IT DRAWS, and the two states that must never look alike ────────────
 *
 * `exchangeRate === null` means NOT YET CONFIRMED. BR-MD-11-11: "a draft
 * document re-reads the newest rate on or before its own date every time it is
 * opened", so a draft has frozen nothing and must not print a figure as though
 * it had. It says "Not frozen until posted", and it cites no date, because
 * there is no dated row the DOCUMENT owns. The base figure beside it is the
 * server's indicative conversion — an aid, offered under a sentence that says
 * it is not a claim. A draft that displayed a rate it does not have would undo
 * the whole point of freezing one.
 *
 * `exchangeRate !== null` is the defended figure: the rate, the date it came
 * from, and the total in the base currency, all as stored on the document.
 * Changing a rate today does not move any of them.
 *
 * ── WHEN IT DRAWS NOTHING ───────────────────────────────────────────────────
 *
 * GEN-116 ("the disclosure must be hidden when no conversion happened") and
 * ADM-01's rule that a single-currency business "should never see the word
 * currency": a document raised in the base currency has converted nothing, so
 * there is no panel at all — not an empty one, not a rate of 1.
 */
export interface FrozenRateView {
  /** The document's own currency. */
  currency: string;
  /** The FROZEN figure; null while the document is a draft. */
  exchangeRate: string | null;
  /** BR-SAL-04-09's "as of" date; null when nothing is frozen. */
  rateEffectiveOn: string | null;
  /** What the company reports in (FIN-05's base). */
  baseCurrency: string;
  /** The grand total converted at the frozen — or indicative — rate. */
  baseTotal: string | null;
  /** BR-MD-11-14's sentence when the rate is more than 7 days old. */
  rateWarning: string | null;
}

export function FxPanel({ doc }: { doc: FrozenRateView | undefined }) {
  const { t } = useTranslation();

  // Nothing to say about a document that has not been saved yet, and nothing
  // to say about one raised in the currency the company reports in.
  if (doc === undefined || doc.currency === doc.baseCurrency) return null;

  return (
    <div className="grid gap-1 rounded-md border border-border bg-muted/40 px-3 py-2 text-xs">
      <div className="flex items-center justify-between">
        <span className="text-muted-foreground">{t('fx.rate')}</span>
        <span className="font-mono">
          {doc.exchangeRate === null ? (
            t('fx.notFrozen')
          ) : (
            <Ltr value={`1 ${doc.baseCurrency} = ${doc.exchangeRate} ${doc.currency}`} />
          )}
        </span>
      </div>
      {doc.rateEffectiveOn !== null ? (
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">{t('fx.asOf')}</span>
          <Ltr value={doc.rateEffectiveOn} />
        </div>
      ) : null}
      {/* `typeof … === 'string'` rather than `!== null` on purpose. The five
          fields are NOT optional on the wire, but a hand-written FIXTURE that
          omits them used to reach <Amount value={undefined}>, which throws
          inside groupAmount and takes the whole editor down — and the failure
          then reads "cannot find button", which points nowhere near the cause.
          The panel is still drawn for such a fixture (loud, and catchable);
          it just does not bring the screen down with it. */}
      {typeof doc.baseTotal === 'string' ? (
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">
            {t('fx.baseTotal', { currency: doc.baseCurrency })}
          </span>
          <Amount value={doc.baseTotal} currency={doc.baseCurrency} />
        </div>
      ) : null}
      {doc.rateWarning !== null && doc.rateWarning !== undefined ? (
        // GEN-117 says the CONVERSION disclosure is grey; this one is
        // BR-MD-11-14's warning, which is a different thing and is amber on
        // purpose.
        <p className="mt-1 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
          {doc.rateWarning}
        </p>
      ) : null}
    </div>
  );
}

/**
 * BR-MD-11-14 at the moment the rule names — the confirming click, not the
 * rendering: "confirming a document in that currency must warn: 'The newest
 * EUR rate is from 2026-06-28. Continue with 0.9180?'"
 *
 * A question, not a refusal, so a No leaves the draft exactly as it was and
 * the answer is false. The SERVER composes the sentence, because the threshold
 * and the rate it names are the server's facts; the screen only asks it.
 *
 * ── WHY THIS IS A HOOK AND NOT THE `window.confirm` IT REPLACED ────────────
 *
 * It used to be `return window.confirm(rateWarning)`. BR-PERM-15 (spec line
 * 35168) forbids that outright — "every irreversible action must use one of
 * these two patterns; a new pattern must not be invented" — and browser chrome
 * is neither of §16.5.3's two, which is the same argument that took twenty-six
 * other sites onto `ConfirmBox`. It carries an OK button besides, which GEN-206
 * forbids by name.
 *
 * A dialog cannot answer synchronously, so the eight editors and one list page
 * that ask this question have to await it. THIS IS NOT A FOURTH CONFIRMATION
 * PATTERN: the dialog below IS `ConfirmBox`, unchanged and unwrapped. The hook
 * exists so nine screens share one wiring instead of nine copies of the same
 * three-line state machine — the identical argument that made `FxPanel` one
 * component and `frozenRateFields` one schema.
 *
 * ── THE FAST PATH IS PRESERVED, AND IT IS THE COMMON CASE ─────────────────
 *
 * MOST saves carry no warning at all: `staleRateWarning` returns null for every
 * base-currency document, every document whose rate is inside the seven-day
 * window, and every draft with nothing frozen. So the no-warning branch below
 * returns the literal `true` — a BOOLEAN, not a promise — and it does it
 * BEFORE touching state, so it schedules no render, mounts no dialog and asks
 * the user for no turn. A conversion that opened a dialog turn on every save
 * would have taxed nine screens to serve the rare case.
 *
 * That property is asserted rather than described, in
 * `rate-warning-gate.test.tsx`: the returned value is `=== true` and a render
 * counter does not move. What it does NOT preserve, stated plainly because the
 * assertion cannot see it: a caller that writes `await` gets one microtask tick
 * it did not have before. That is a tick, not a turn — no render, no dialog, no
 * user.
 *
 * ── RE-ENTRY, WHICH `window.confirm` MADE IMPOSSIBLE AND A DIALOG DOES NOT ─
 *
 * `window.confirm` blocked the whole page, so a second click could not happen.
 * A real browser's `showModal()` puts this dialog in the top layer and blocks
 * the button underneath just as firmly — but jsdom implements no `showModal`
 * (see `confirm-box.tsx`), so nothing in a test does. Rather than leave the
 * behaviour to whichever engine is running, a second ask while one is open is
 * answered `false` and nothing is written: an unanswered question is a No.
 *
 * A pending ask whose screen unmounts never settles, so its caller's
 * continuation never runs. That is the correct outcome — the screen is gone —
 * and it is why the promise is deliberately not rejected: a rejection would
 * surface as an unhandled error on a page the user has already left.
 */
export interface RateWarningGate {
  /**
   * `true`/`false` for "go on" and "leave the draft alone", so every call site
   * reads `if (!(await confirmRateWarning(doc?.rateWarning))) return;` — the
   * one `await` being the only thing that changed at any of the nine.
   */
  confirmRateWarning: (rateWarning: string | null | undefined) => boolean | Promise<boolean>;
  /** GEN-205's box. Render it once, anywhere inside the screen. */
  rateWarningDialog: ReactNode;
}

export function useRateWarningGate(): RateWarningGate {
  const { t } = useTranslation();
  const [ask, setAsk] = useState<{ sentence: string; answer: (proceed: boolean) => void } | null>(
    null,
  );

  // Whether a question is on screen, in a ref rather than read off `ask`:
  // `confirmRateWarning` is stable across renders (it is what call sites put in
  // dependency arrays), so it cannot close over a state value without going
  // stale. A ref is the same fact with no render attached — and reading it
  // costs the fast path nothing.
  const open = useRef(false);

  const confirmRateWarning = useCallback(
    (rateWarning: string | null | undefined): boolean | Promise<boolean> => {
      // THE FAST PATH. No state, no render, no dialog — see the block above.
      if (rateWarning === null || rateWarning === undefined) return true;
      // Re-entry, answered No. Never a second dialog and never a dropped
      // promise: the first question is still on screen and still governs.
      if (open.current) return false;
      open.current = true;
      return new Promise<boolean>((answer) => setAsk({ sentence: rateWarning, answer }));
    },
    [],
  );

  // The pending ask is read from the CLOSURE, not from inside the state
  // updater: an updater must be pure and React calls it twice in StrictMode,
  // which would answer one promise twice.
  const close = (proceed: boolean) => {
    open.current = false;
    setAsk(null);
    ask?.answer(proceed);
  };

  return {
    confirmRateWarning,
    rateWarningDialog: (
      <ConfirmBox
        open={ask !== null}
        // GEN-205's four things. The BODY is the server's sentence, passed
        // through with not one word changed — it is the same text
        // `window.confirm` displayed, and the same text `FxPanel` draws in
        // amber on the document itself.
        //
        // It arrives in English whatever the user reads in:
        // `FX_MESSAGES.staleRate` (packages/contracts/src/fx.ts:134) composes
        // it from three values, and `localizeServerMessage` maps whole
        // sentences to keys by EXACT MATCH (lib/server-messages.ts:251), which
        // a parameterised sentence can never satisfy. That is a live defect
        // held by another wave and it is deliberately NOT patched here: this
        // conversion changes the chrome and must not quietly change the words.
        title={t('fx.staleRateTitle')}
        body={ask?.sentence ?? ''}
        // GEN-206's verb, never OK — which is the second thing browser chrome
        // could not give this question.
        confirmLabel={t('fx.staleRateContinue')}
        onConfirm={() => close(true)}
        // GEN-207: Escape, the backdrop and Cancel are one outcome, and here
        // that outcome is BR-MD-11-14's No — the draft is left exactly as it
        // was, which is what the three tests in `fx-screens.test.tsx` measure
        // by counting non-GET requests.
        onCancel={() => close(false)}
      />
    ),
  };
}
